export default function ReelPilotSupport() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Support - ReelPilot
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Help, troubleshooting, and contact details for ReelPilot.
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <h2 className="text-2xl font-bold text-[#A8720E]">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I make a reel or short?
              </h3>
              <p className="mt-1">
                Import your clips, then trim and arrange them on the timeline.
                Add background music, drop in text overlays, apply filters, and
                tweak speed. When you are happy, pick an aspect ratio and export
                — there is no watermark on the finished video.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I add or auto-generate captions?
              </h3>
              <p className="mt-1">
                Open the captions tool and choose auto-captions to have ReelPilot
                listen to your video and write the captions for you, or add and
                edit caption text yourself. Auto-captioning runs entirely on your
                device using the on-device caption engine — nothing is uploaded —
                so it works even without an internet connection.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Which aspect ratios are supported?
              </h3>
              <p className="mt-1">
                You can export in 9:16 for reels and shorts, 1:1 for square
                posts, and 16:9 for widescreen. Switch the ratio at any time and
                reframe your clips to fit.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                What video formats can I import?
              </h3>
              <p className="mt-1">
                ReelPilot works with common video formats such as MP4 and MOV,
                along with the standard photo formats for adding images to your
                timeline. If a clip will not import, try recording or saving it
                in MP4 and import it again.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Does the app work offline?
              </h3>
              <p className="mt-1">
                Yes. Editing, captioning, and exporting all happen on your
                device, so the core of the app works offline. Internet access is
                only needed for services such as ads and analytics.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Will my videos stay private?
              </h3>
              <p className="mt-1">
                Yes. Your videos, photos, audio, and captions are processed
                entirely on your device. The app does not upload your media to
                our servers — the only data that leaves the device is anonymous
                analytics and crash diagnostics.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                What does the Pro upgrade include?
              </h3>
              <p className="mt-1">
                ReelPilot is free to use. A single one-time Pro upgrade removes
                ads. There is no subscription and no recurring charge.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Why am I seeing ads?
              </h3>
              <p className="mt-1">
                Some versions of the app show ads to support ongoing development,
                maintenance, and feature updates. The one-time Pro upgrade
                removes them.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Export failed or the app crashed — what should I do?
              </h3>
              <p className="mt-1">
                Make sure you are on the latest version from Google Play, then
                fully close and reopen the app. Exporting longer or
                higher-resolution videos uses more memory and storage, so free up
                some space on your device and try again. If the problem
                continues, email us the details below and we will help.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#A8720E]">Contact Support</h2>
          <p>
            If you need help, have feedback, or want to report an issue, email{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            . We typically respond within 24-48 hours.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Report a Bug</h2>
          <p>
            To help us resolve issues faster, include the following in your
            message when possible:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Your device model and OS version</li>
            <li>The app version shown in Settings</li>
            <li>A short description of the issue</li>
            <li>Steps to reproduce the problem</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">App Information</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>App Name:</strong> ReelPilot
            </li>
            <li>
              <strong>Developer:</strong> Cosmovex
            </li>
            <li>
              <strong>Contact Email:</strong>{" "}
              <a
                href="mailto:trionapps@gmail.com"
                className="text-[#0F6BBF] underline"
              >
                trionapps@gmail.com
              </a>
            </li>
            <li>
              <strong>Privacy Policy:</strong>{" "}
              <a
                href="https://cosmovexapps.com/apps/reelpilot/privacy-policy"
                className="text-[#0F6BBF] underline"
              >
                https://cosmovexapps.com/apps/reelpilot/privacy-policy
              </a>
            </li>
            <li>
              <strong>Support URL:</strong>{" "}
              <a
                href="https://cosmovexapps.com/apps/reelpilot/support"
                className="text-[#0F6BBF] underline"
              >
                https://cosmovexapps.com/apps/reelpilot/support
              </a>
            </li>
          </ul>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-center text-sm text-[#8A93A2]">
          © {new Date().getFullYear()} Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
